import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { venueMapsUrl } from "@/lib/venueMaps";
import axios from "axios";
import DocumentTitle from "@/components/DocumentTitle";
import { PosterViewer, CompactCard, VerticalCard, useWidthBand } from "./feedCard";
import { IkoMix } from "@/design-system";
import { ArrowLeft, MapPin } from "lucide-react";
import useSafeBack from "@/hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });

export default function VenuePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const { band, cols } = useWidthBand();
  const onBack = useSafeBack("/app");

  useEffect(() => {
    setLoading(true);
    client.get(`/public/venues/${id}`)
      .then((r) => { setVenue(r.data.venue); setEvents(r.data.events || []); })
      .catch(async () => {
        // Wave 6 #28 · when the venue is not found, check if it was merged
        // into another venue and redirect to the keeper before showing 404.
        try {
          const rr = await client.get(`/public/venues/resolve/${encodeURIComponent(id)}`);
          if (rr.data?.redirected && rr.data.resolved_to && rr.data.resolved_to !== id) {
            navigate(`/app/venue/${rr.data.resolved_to}`, { replace: true });
            return;
          }
        } catch { /* fall through to 404 */ }
        setVenue(null);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const Card = band === "s" || band === "m" ? CompactCard : VerticalCard;
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const gridClassName = isDesktop ? "ww-grid-cards-3" : undefined;
  const gridStyle = isDesktop ? undefined : {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: cols === 1 ? 12 : 16,
  };

  const mapsUrl = venue ? venueMapsUrl(venue) : null;

  return (
    <div className="flex h-full flex-col" data-testid="venue-page" style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={venue?.name ? `${venue.name.toLowerCase()} · watu wako` : "watu wako"} />
      <div className="flex-1 overflow-y-auto pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="ww-container">
          <button type="button" onClick={onBack} data-testid="venue-back" aria-label="back"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ww-text-muted)",
                           fontFamily: "Arial", fontSize: 13, padding: "10px 4px", minHeight: 44,
                           background: "transparent", border: 0, cursor: "pointer" }}>
            <ArrowLeft size={16} /> back
          </button>

          {loading && <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>}
          {!loading && !venue && (
            <div className="py-16 text-center">
              <h1 style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 30, textTransform: "lowercase" }}>
                venue not found
              </h1>
              <p className="mt-2" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>
                see what's on this weekend.
              </p>
              <Link to="/app" data-testid="venue-cta-foryou"
                    style={{ display: "inline-block", marginTop: 16, padding: "10px 20px", borderRadius: 999,
                             background: "#FFD60A", color: "#1A1A30",
                             fontFamily: "Poppins", fontWeight: 600, textDecoration: "none" }}>
                open for you
              </Link>
            </div>
          )}

          {venue && (
            <div style={isDesktop
              ? { display: "grid", gridTemplateColumns: "320px minmax(0, 1fr)", gap: 32, marginTop: 16 }
              : { marginTop: 12 }}>
              <aside style={isDesktop ? { position: "sticky", top: 24, alignSelf: "start" } : undefined}>
                <header>
                  <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                              fontSize: 11, color: "var(--ww-text-muted)" }}>venue</p>
                  <h1 data-testid="venue-name" className="mt-1 lowercase"
                      style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                               fontSize: 30, lineHeight: 1.02 }}>
                    {(venue.name || "").toLowerCase()}
                  </h1>
                  {venue.area && (
                    <p data-testid="venue-area" className="mt-1"
                       style={{ fontFamily: "Arial", fontSize: 15, color: "var(--ww-text-muted)" }}>
                      {venue.area.toLowerCase()}
                    </p>
                  )}
                  {(venue.address || venue.name) && mapsUrl && (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="venue-maps"
                       style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10,
                                padding: "10px 14px", borderRadius: 999,
                                border: "1px solid var(--ww-accent-ink)", color: "var(--ww-text)",
                                fontFamily: "Poppins", fontWeight: 600, textDecoration: "none",
                                fontSize: 13, minHeight: 44 }}>
                      <MapPin size={14} /> open in maps
                    </a>
                  )}
                  <div className="mt-3">
                    <IkoMix counts={venue.iko_mix?.counts || {}} total={venue.iko_mix?.total || 0}
                            overrideChips={venue.iko_chip_override} label="usually hosts"
                            testId="venue-iko-mix" />
                  </div>
                </header>
              </aside>
              <div style={{ minWidth: 0 }}>
                <h2 className="mb-3 lowercase"
                    style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                             fontSize: 22, lineHeight: 1.05 }}>
                  upcoming here
                </h2>
                {events.length === 0 ? (
                  <div className="rounded-2xl border p-8 text-center"
                       data-testid="venue-empty"
                       style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
                    <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 14 }}>
                      no upcoming events at this venue yet.
                    </p>
                    <Link to="/app" data-testid="venue-empty-cta"
                          style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 999,
                                   background: "#FFD60A", color: "#1A1A30",
                                   fontFamily: "Poppins", fontWeight: 600, textDecoration: "none" }}>
                      see what's on this weekend
                    </Link>
                  </div>
                ) : (
                  <div className={gridClassName} style={gridStyle}>
                    {events.map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
